import React, { useEffect, useRef } from 'react';
import { loadLeaflet } from '../../lib/leaflet';

export interface ProjectLocation {
  lat: number;
  lon: number;
  /** Optional label shown in a popup when the pin is clicked. */
  name?: string;
}

interface ProjectLocationMapProps {
  locations: ProjectLocation[];
  /** Map height in pixels (default: 400). */
  height?: number;
}

// CARTO basemaps require an API key on production domains, matching the flights
// map (FlightMapPreview). Without it, tiles can be blocked in production.
const cartoBasemapsApiKey = process.env.REACT_APP_CARTO_BASEMAPS_API_KEY;
const CARTO_TILE_URL = `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${
  cartoBasemapsApiKey ? `?key=${encodeURIComponent(cartoBasemapsApiKey)}` : ''
}`;

const PIN_HTML = `<div style="
  width: 16px;
  height: 16px;
  background-color: var(--wren-orange);
  border: 2px solid white;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
"></div>`;

/**
 * Renders a Leaflet map with a pin for every provided location, auto-fitting the
 * view to show all pins. Used for Contentful `worldMap` entries, which can hold
 * multiple `projectLocationPin`s. Returns `null` when there are no valid
 * coordinates to plot.
 */
export const ProjectLocationMap = ({
  locations,
  height = 400,
}: ProjectLocationMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstanceRef = useRef<any>(null);

  const validLocations = locations.filter(
    (l) => Number.isFinite(l?.lat) && Number.isFinite(l?.lon)
  );
  // Stable dependency so the effect re-runs when coordinates or popup labels
  // change (editing a pin's name must refresh the marker popup).
  const locationsKey = validLocations
    .map((l) => `${l.lat},${l.lon},${l.name ?? ''}`)
    .join('|');

  useEffect(() => {
    if (!mapRef.current || validLocations.length === 0) return;
    let mounted = true;

    loadLeaflet()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((L: any) => {
        if (!mounted || !mapRef.current) return;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }

        const map = L.map(mapRef.current, { scrollWheelZoom: false });
        mapInstanceRef.current = map;

        L.tileLayer(CARTO_TILE_URL, {
          attribution:
            '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
          maxZoom: 19,
          crossOrigin: 'anonymous',
        }).addTo(map);

        const latlngs: Array<[number, number]> = [];
        validLocations.forEach((loc) => {
          const marker = L.marker([loc.lat, loc.lon], {
            icon: L.divIcon({
              className: 'project-location-pin',
              html: PIN_HTML,
              iconSize: [16, 16],
              iconAnchor: [8, 16],
            }),
          }).addTo(map);
          if (loc.name) marker.bindPopup(loc.name);
          latlngs.push([loc.lat, loc.lon]);
        });

        if (latlngs.length === 1) {
          map.setView(latlngs[0], 5);
        } else {
          map.fitBounds(latlngs, { padding: [40, 40] });
        }
      })
      .catch(() => {
        // Leaflet failed to load — leave the container empty rather than erroring.
      });

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // Re-run only when coordinates change (validLocations is derived from locationsKey).
  }, [locationsKey]);

  if (validLocations.length === 0) return null;

  return (
    <div
      ref={mapRef}
      className="w-full overflow-hidden rounded-lg"
      style={{ height }}
    />
  );
};
